import React, { createContext, useContext, useEffect, useState } from 'react';
import AACChromeInternalAPI, {
  InternalChromeConfig,
} from '@amzn/aac-chrome-internal-api';
import { getChromeData } from 'src/commons/apis';
import { InternalChromeApiMetricPublisher } from 'src/commons/utils/metrics';
import { logger } from 'src/commons/utils/logger';
import { isBtswContext, isFromBtswWc, isFromTrim } from 'src/commons/utils';
import { LoadingStatus } from 'src/commons/types';
import { updateInternalChromeConfig } from 'src/app/slice';
import { store } from 'src/app/store';
import { getTrimInternalChromeConfig } from 'src/commons/utils/helpers';

export { InternalChromeConfig, Feature } from '@amzn/aac-chrome-internal-api';

export interface InternalChromeConfigContextState {
  loadingStatus: LoadingStatus;
  config: InternalChromeConfig;
}

// Exported so tests / Storybook can seed the internal chrome config (e.g.
// featureLaunchInfo flags) that the provider otherwise fills
// from async fetches which do not resolve in Storybook.
export const InternalChromeConfigContext =
  createContext<InternalChromeConfigContextState>({
    loadingStatus: 'loading',
    config: {} as InternalChromeConfig,
  });

export const useInternalChromeConfigContext =
  (): InternalChromeConfigContextState => {
    const context = useContext(InternalChromeConfigContext);
    if (context === undefined) {
      throw new Error(
        'useInternalChromeConfigContext must be used within a InternalChromeConfigContextProvider',
      );
    }
    return context;
  };

/**
 * Both sources carry the same runtime payload as InternalChromeConfig but are
 * typed more loosely — the window getter can be undefined and
 * AACChromeInternalAPI returns ChromeContext, whose fields are nearly all
 * optional. Narrow once here so callers (and useState below) get one concrete
 * shape instead of a three-way union.
 */
const getInternalChromeContext = (): InternalChromeConfig => {
  try {
    return window.AACChrome.InternalAPI.getChromeContext() as InternalChromeConfig;
  } catch (e) {
    return AACChromeInternalAPI.getChromeContext() as InternalChromeConfig;
  }
};

export const InternalChromeConfigContextProvider: React.FC = ({ children }) => {
  const isFromTrimValue = isFromTrim();
  const isAjaxUsed = !isFromTrimValue && !isBtswContext() && !isFromBtswWc();

  const initialContext = isAjaxUsed
    ? ({} as InternalChromeConfig)
    : isFromTrimValue
      ? // Trim seeds this via InternalChromeConfigHandler, but the getter is
        // typed as possibly-undefined; fall back to the same empty config the
        // ajax path starts from rather than letting undefined reach useState.
        (getTrimInternalChromeConfig() ?? ({} as InternalChromeConfig))
      : getInternalChromeContext();

  const [loadingStatus, setLodingStatus] = useState<LoadingStatus>(
    isAjaxUsed ? 'loading' : 'success',
  );
  const [config, setConfig] = useState<InternalChromeConfig>(initialContext);

  const ajaxLoadInternalChromeConfigData = async () => {
    try {
      const { internalChromeConfig } = await getChromeData();
      setConfig(internalChromeConfig);
      setLodingStatus('success');
      InternalChromeApiMetricPublisher.publishCounterMonitor('initFailure', 0);
    } catch (e: any) {
      setLodingStatus('error');
      InternalChromeApiMetricPublisher.publishCounterMonitor('initFailure', 1);
      logger.error('failed to fetch chromeData:', e);
    }
  };

  useEffect(() => {
    if (loadingStatus === 'loading') {
      ajaxLoadInternalChromeConfigData();
    } else if (loadingStatus === 'success') {
      // cannot pass in the entire 'config' object since it is not serializable (contains getters, setters etc.)
      // only pass in required fields to the redux store
      //
      // Omit keys whose value is undefined. `updateInternalChromeConfig` spreads
      // this payload over `state.internalChromeContext`, and all five chrome
      // islands share ONE Redux singleton (src/app/store). A key present with an
      // `undefined` value overwrites an already-resolved sibling value on spread,
      // so a provider instance that reaches this dispatch before its own config
      // has resolved would blank fields another island already populated (e.g.
      // deviceType). Sending only the defined keys makes the dispatch additive:
      // it can fill fields but never clear them.
      const definedFields = Object.fromEntries(
        Object.entries({
          deviceType: config.deviceType,
          isMobileWebView: config.isMobileWebView,
          forceDesktopSite: config.forceDesktopSite,
          entityId: config.entityId,
          isInternalView: config.isInternalView,
        }).filter(([, value]) => value !== undefined),
      ) as Partial<InternalChromeConfig>;

      store.dispatch(
        updateInternalChromeConfig(definedFields as InternalChromeConfig),
      );
    }
  }, [loadingStatus]);

  return (
    <InternalChromeConfigContext.Provider
      value={{
        loadingStatus,
        config,
      }}
    >
      {children}
    </InternalChromeConfigContext.Provider>
  );
};
